import { useState } from 'react';
import ServiceNavbar from '../shared/ServiceNavbar';
import ServiceHero from '../shared/ServiceHero';
import ServiceFAQ from '../shared/ServiceFAQ';
import ServiceCTABlock from '../shared/ServiceCTABlock';
import Footer from '../../home/components/Footer';

const commonIssues = [
  {
    icon: 'ri-flashlight-fill',
    problem: 'Breakers Tripping Repeatedly',
    signal: 'Urgent',
    signalColor: '#EF4444',
    desc: 'A breaker that trips again and again signals an overloaded circuit, a short, or a failing breaker — each needs professional diagnosis.',
  },
  {
    icon: 'ri-plug-2-fill',
    problem: 'Dead or Sparking Outlets',
    signal: 'Safety Risk',
    signalColor: '#EF4444',
    desc: 'Outlets that don\'t work or show visible sparks may indicate loose wiring, failed devices, or arcing — a fire risk that needs immediate attention.',
  },
  {
    icon: 'ri-sun-foggy-fill',
    problem: 'Flickering or Dimming Lights',
    signal: 'Investigate',
    signalColor: '#D97706',
    desc: 'Flickering lights can point to loose connections, overloaded circuits, or utility-side issues. We identify the actual cause, not just swap bulbs.',
  },
  {
    icon: 'ri-dashboard-3-fill',
    problem: 'Outdated or Full Panel',
    signal: 'Upgrade Needed',
    signalColor: '#D97706',
    desc: 'A full or aging panel limits what you can add and may not meet modern safety codes. We assess and upgrade panels to current standards.',
  },
  {
    icon: 'ri-temp-hot-fill',
    problem: 'Warm Outlets or Switch Plates',
    signal: 'Act Now',
    signalColor: '#EF4444',
    desc: 'Heat at an outlet or switch face means electricity is leaking somewhere it shouldn\'t. This is a direct fire hazard that needs same-day attention.',
  },
  {
    icon: 'ri-home-gear-fill',
    problem: 'GFCI Outlets Won\'t Reset',
    signal: 'Code Issue',
    signalColor: '#D97706',
    desc: 'A GFCI that won\'t hold its reset likely has a wiring fault on the circuit it\'s protecting. We trace and fix the underlying problem properly.',
  },
];

const processSteps = [
  { icon: 'ri-phone-fill', num: '01', title: 'You Call or Text', desc: 'Describe what you\'re experiencing. We listen carefully, ask the right questions, and tell you honestly whether it\'s urgent or can be scheduled.' },
  { icon: 'ri-calendar-line', num: '02', title: 'We Schedule Fast', desc: 'We respect your time. Standard service calls are typically scheduled within 1–2 business days. Safety issues get prioritized.' },
  { icon: 'ri-search-eye-fill', num: '03', title: 'Proper Diagnosis', desc: 'We identify the root cause — not just the symptom. We don\'t guess or recommend repairs we haven\'t confirmed are needed.' },
  { icon: 'ri-price-tag-3-fill', num: '04', title: 'Transparent Quote', desc: 'You receive a clear price before any work begins. No verbal estimates, no vague ranges. What we quote is what you pay.' },
  { icon: 'ri-tools-fill', num: '05', title: 'Clean, Correct Repair', desc: 'Licensed work, done right the first time. We bring all standard parts and materials so most repairs are completed same-visit.' },
];

const faqs = [
  {
    question: 'How quickly can I expect a response to my service call?',
    answer: 'For standard service and repair calls, we typically respond within 1 business day and can often schedule within 1–2 days. For urgent situations — burning smells, repeated sparking, or loss of power to critical areas — let us know the urgency when you call and we\'ll prioritize accordingly.',
  },
  {
    question: 'Will you give me a price before starting any work?',
    answer: 'Always. We provide a written price before a single screw is turned. There are no verbal estimates that change once we\'re in your home, and no "we found more problems" surprises added to your bill without your explicit approval. Transparent pricing is non-negotiable for us.',
  },
  {
    question: 'What if you find additional issues while diagnosing?',
    answer: 'We\'ll point out anything we notice, explain what it means, and give you options — but we never do additional work without your direct approval. It\'s your home and your decision. We believe in giving you information, not pressure.',
  },
  {
    question: 'Do I need to be home during the entire service call?',
    answer: 'It\'s helpful to be available at the start so we can discuss what you\'re experiencing and walk through the relevant areas. For longer jobs, we\'re comfortable working independently once we\'ve connected with you and confirmed the scope — just let us know your preference.',
  },
  {
    question: 'How do you handle cleanup after electrical work?',
    answer: 'We treat your home with the same care we\'d want for our own. Shoe covers go on at the door. We lay drop cloths where needed, clean up all debris, and leave the work area at least as clean as we found it. This is a standard part of how we operate — not something you should have to request.',
  },
  {
    question: 'Can you handle both small repairs and large electrical projects?',
    answer: 'Yes. Whether it\'s a single outlet repair or a full panel replacement and whole-home rewire, we bring the same professionalism to every job. No job is too small to do well, and no job is too large for our team to handle properly.',
  },
];

const ResidentialRepairPage = () => {
  const [activeIssue, setActiveIssue] = useState<number | null>(null);
  void activeIssue;
  void setActiveIssue;

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <ServiceNavbar />

      <ServiceHero
        bgImage="https://public.readdy.ai/ai/img_res/534bf1efbc92325f813d4095a82ddecc.jpg"
        label="Residential Service & Repair"
        headline="Your Home Deserves"
        headlineAccent="Honest Electrical Work"
        subtext="Fast response, accurate diagnosis, upfront pricing, and repairs done right the first time. We treat your home like it's ours — because that's the standard we hold ourselves to."
        trustBadges={[
          { icon: 'ri-shield-star-fill', label: 'Licensed & Insured' },
          { icon: 'ri-heart-fill', label: 'Respect for Your Home' },
          { icon: 'ri-award-fill', label: '20+ Years Experience' },
          { icon: 'ri-chat-check-fill', label: 'Clear Communication' },
        ]}
      />

      {/* ── TRUST BAND ──────────────────────────────────────────── */}
      <section className="bg-[#0A1628] py-0">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-[#1E3048]">
            {[
              { icon: 'ri-price-tag-3-fill', label: 'Upfront Pricing', sub: 'No surprises ever' },
              { icon: 'ri-time-fill', label: 'Fast Scheduling', sub: '1–2 business day response' },
              { icon: 'ri-home-heart-fill', label: 'Home Respect', sub: 'Shoe covers, full cleanup' },
              { icon: 'ri-medal-fill', label: 'No Upsells', sub: 'We fix what you need' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-4 px-6 lg:px-8 py-6">
                <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#00D9FF]/10 flex-shrink-0">
                  <i className={`${item.icon} text-[#00D9FF] text-base`} />
                </span>
                <div>
                  <div className="text-white text-[13px] font-bold">{item.label}</div>
                  <div className="text-[#3A566E] text-[11px]">{item.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMMON ISSUES ───────────────────────────────────────── */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <span className="inline-block text-[#00D9FF] text-[11px] font-semibold uppercase tracking-[3px] mb-4">Sound Familiar?</span>
            <h2 className="text-3xl lg:text-4xl font-black text-[#0A1628] mb-4 leading-tight">
              Common Reasons Homeowners<br />Call DOC Electric
            </h2>
            <p className="text-[#5A7A96] text-[15px] leading-relaxed">
              Most electrical problems in the home start as small signals before they become bigger hazards. If any of these sound familiar, it&apos;s worth a call.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {commonIssues.map((issue, i) => (
              <div
                key={i}
                className="group relative bg-[#F4F8FC] hover:bg-white rounded-xl p-6 border border-[#E0E8F0] hover:border-[#B0CDE0] hover:shadow-[0_8px_32px_rgba(0,0,0,0.07)] transition-all duration-300 cursor-default overflow-hidden"
              >
                <div className="absolute top-0 left-0 right-0 h-[3px] rounded-t-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: `linear-gradient(to right, ${issue.signalColor}60, transparent)` }}
                />
                <div className="flex items-start justify-between mb-4">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#0A1628]">
                    <i className={`${issue.icon} text-[#00D9FF] text-base`} />
                  </span>
                  <span
                    className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                    style={{ color: issue.signalColor, backgroundColor: `${issue.signalColor}14`, border: `1px solid ${issue.signalColor}30` }}
                  >
                    {issue.signal}
                  </span>
                </div>
                <h3 className="text-[#0A1628] text-[15px] font-bold mb-2">{issue.problem}</h3>
                <p className="text-[#5A7A96] text-[13px] leading-[1.7]">{issue.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-[#5A7A96] text-[14px] mb-4">Don&apos;t see your issue? We handle all residential electrical repairs.</p>
            <a href="tel:+12693700765" className="inline-flex items-center gap-2 text-[#00D9FF] font-bold text-[14px] hover:underline cursor-pointer">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-fill" /></span>
              Call (269) 370-0765
            </a>
          </div>
        </div>
      </section>

      {/* ── OVERVIEW EDITORIAL ──────────────────────────────────── */}
      <section className="bg-[#0A1628] py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {/* Pull quote — centered editorial */}
          <div className="max-w-3xl mx-auto text-center mb-20">
            <div className="w-12 h-[2px] bg-[#00D9FF] mx-auto mb-8" />
            <blockquote className="text-2xl lg:text-3xl font-bold text-white leading-[1.5] mb-6">
              &ldquo;We&apos;re not here to find things to sell you. We&apos;re here to fix what&apos;s wrong, tell you what we found, and leave your home in better shape than we arrived.&rdquo;
            </blockquote>
            <p className="text-[#00D9FF] text-[13px] font-semibold uppercase tracking-[2px]">
              — DOC Electric LLC, Kalamazoo
            </p>
            <div className="w-12 h-[2px] bg-[#00D9FF] mx-auto mt-8" />
          </div>

          {/* 2-col service description */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div>
              <span className="inline-block text-[#00D9FF] text-[11px] font-semibold uppercase tracking-[3px] mb-5">The Full Service</span>
              <h2 className="text-2xl lg:text-3xl font-black text-white mb-5 leading-tight">
                Everything from a Single Outlet<br />to a Full Rewire
              </h2>
              <p className="text-[#5E7A96] text-[15px] leading-[1.8] mb-6">
                Residential electrical repair covers an enormous range of work. One week it&apos;s a faulty outdoor outlet; the next it&apos;s a Federal Pacific panel that&apos;s been a code violation since the house was built in 1978. DOC Electric handles all of it with the same commitment to doing it right.
              </p>
              <p className="text-[#5E7A96] text-[15px] leading-[1.8]">
                We don&apos;t subcontract, cut corners, or recommend repairs that aren&apos;t justified. Our team is licensed, honest, and technically sharp — which is exactly what electrical work in your home requires.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                'Tripping circuit breaker diagnosis',
                'Outlet and switch replacement',
                'GFCI & AFCI upgrades',
                'Panel replacements & upgrades',
                'Whole-home rewiring',
                'Meter socket repair',
                'Smoke detector installation',
                'Ceiling fan wiring',
                'Flickering light diagnosis',
                'Outdoor & landscape circuits',
                'Dedicated circuit installation',
                'Aluminum wiring remediation',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2.5 py-2.5 border-b border-[#1E3048]">
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-double-line text-[#00D9FF] text-sm" />
                  </span>
                  <span className="text-[#7A9AB8] text-[13px]">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PROCESS — CARD STEPS ────────────────────────────────── */}
      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <span className="inline-block text-[#00D9FF] text-[11px] font-semibold uppercase tracking-[3px] mb-4">How a Service Call Works</span>
            <h2 className="text-3xl lg:text-4xl font-black text-[#0A1628] mb-4 leading-tight">Simple, Transparent,<br />From Call to Completion</h2>
            <p className="text-[#5A7A96] text-[15px] max-w-xl mx-auto">No surprises, no pressure, no unnecessary work. Here&apos;s exactly what to expect when you call DOC Electric.</p>
          </div>

          {/* Horizontal card steps */}
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-3">
            {processSteps.map((step, i) => (
              <div key={i} className="relative group">
                {/* Connector line (desktop only) */}
                {i < processSteps.length - 1 && (
                  <div className="absolute top-[32px] left-[calc(100%-1px)] w-full h-[2px] bg-gradient-to-r from-[#E0E8F0] to-[#E0E8F0] hidden lg:block z-0 pointer-events-none" style={{ width: 'calc(100% - 48px)', left: 'calc(48px + (100% - 48px)/2 + 8px)' }} />
                )}
                <div className="relative z-10 bg-[#F7F9FC] group-hover:bg-white rounded-xl p-5 border border-[#E0E8F0] group-hover:border-[#00D9FF]/30 group-hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] transition-all duration-300 h-full flex flex-col">
                  {/* Number + Icon */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#0A1628] flex-shrink-0">
                      <i className={`${step.icon} text-[#00D9FF] text-sm`} />
                    </div>
                    <span className="text-[11px] font-black text-[#00D9FF] tracking-wider">{step.num}</span>
                  </div>
                  <h3 className="text-[14px] font-bold text-[#0A1628] mb-2 leading-tight">{step.title}</h3>
                  <p className="text-[#5A7A96] text-[12px] leading-[1.7] flex-1">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── THE DOC DIFFERENCE — SPLIT BENEFITS ──────────────── */}
      <section className="bg-[#F4F7FA] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left — image with floating card */}
            <div className="relative order-2 lg:order-1">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20licensed%20electrician%20working%20neatly%20on%20residential%20electrical%20panel%20inside%20modern%20bright%20home%20kitchen%20background%20organized%20clean%20tidy%20professional%20respectful%20homeowner%20trusts%20licensed%20expert%20michigan%20residential%20electrical&width=700&height=640&seq=rr2-diff&orientation=portrait"
                alt="DOC Electric residential service"
                className="w-full h-[500px] object-cover object-top rounded-2xl"
              />
              {/* Star review overlay */}
              <div className="absolute bottom-8 -right-4 lg:-right-8 bg-white rounded-xl p-5 border border-[#E0E8F0] shadow-[0_8px_40px_rgba(0,0,0,0.12)] max-w-[220px]">
                <div className="flex gap-0.5 mb-2">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <span key={s} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-amber-400 text-sm" />
                    </span>
                  ))}
                </div>
                <p className="text-[#0A1628] text-[13px] font-semibold leading-snug mb-2">
                  &ldquo;On time, honest, and left no mess. Will absolutely call again.&rdquo;
                </p>
                <p className="text-[#8BA8C8] text-[11px]">— M. Hendricks, Portage MI</p>
              </div>
              {/* License badge */}
              <div className="absolute top-6 left-6 bg-[#0A1628]/90 backdrop-blur-sm rounded-xl px-4 py-3 border border-[#00D9FF]/20">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-shield-star-fill text-[#00D9FF] text-base" />
                  </span>
                  <span className="text-white text-[12px] font-bold">Michigan Licensed</span>
                </div>
                <div className="text-[#3A566E] text-[11px] mt-0.5">Fully insured, code-compliant</div>
              </div>
            </div>

            {/* Right — benefit list */}
            <div className="order-1 lg:order-2">
              <span className="inline-block text-[#00D9FF] text-[11px] font-semibold uppercase tracking-[3px] mb-5">The DOC Electric Difference</span>
              <h2 className="text-3xl lg:text-4xl font-black text-[#0A1628] mb-5 leading-tight">
                Electrical Service That<br />Actually Respects You
              </h2>
              <p className="text-[#5A7A96] text-[15px] leading-[1.8] mb-8">
                We built our business on residential service calls — one homeowner at a time. These aren&apos;t marketing phrases. They&apos;re the actual standards we hold every technician on our team to.
              </p>

              <div className="flex flex-col gap-5">
                {[
                  {
                    icon: 'ri-shield-check-fill',
                    title: 'No Pressure, No Upsells',
                    desc: 'We fix what you need fixed. If we notice something else worth knowing about, we&apos;ll mention it once — and leave the decision entirely to you.',
                  },
                  {
                    icon: 'ri-search-eye-fill',
                    title: 'Root-Cause Diagnosis',
                    desc: 'We don&apos;t replace parts hoping it fixes the problem. We diagnose properly, identify the actual cause, and repair it correctly.',
                  },
                  {
                    icon: 'ri-home-heart-fill',
                    title: 'Shoe Covers & Full Cleanup',
                    desc: 'Your floors, your furniture, your home. We respect it. Shoe covers on at the door, drop cloths down, everything cleaned up before we leave.',
                  },
                  {
                    icon: 'ri-time-fill',
                    title: 'On Time — Every Visit',
                    desc: 'We arrive when we commit to. If anything changes, we contact you in advance — because your time matters.',
                  },
                  {
                    icon: 'ri-chat-check-fill',
                    title: 'Plain Language Communication',
                    desc: 'We explain what we found and what we recommend in plain terms. No technical jargon designed to confuse. You make the call — informed.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4 group">
                    <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#0A1628] flex-shrink-0">
                      <i className={`${item.icon} text-[#00D9FF] text-lg`} />
                    </span>
                    <div>
                      <h3 className="text-[#0A1628] text-[14px] font-bold mb-1">{item.title}</h3>
                      <p className="text-[#5A7A96] text-[13px] leading-[1.7]" dangerouslySetInnerHTML={{ __html: item.desc }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ServiceFAQ faqs={faqs} theme="light" />

      <ServiceCTABlock
        servicePreset="Residential Service & Repair"
        headline="Let's Get It Fixed"
        subtext="Book a service call with Kalamazoo's most trusted residential electricians. We&apos;ll diagnose it right, price it honestly, and fix it the first time."
      />
      <Footer />
    </div>
  );
};

export default ResidentialRepairPage;
